import {
  Controller,
  FormProvider,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import __ from "src/utils/Translations";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { NewLinkFormModels } from "src/Pages/Company/Dashboard/pages/Widgets/NewLinkForm/NewLinkForm.models";
import IconActionMenu from "src/Pages/Company/Dashboard/components/IconActionMenu";
import DefaultIconWithPaper from "src/Pages/Company/Dashboard/pages/Widgets/shared/DefaultIconWithPaper";
import IconCropper from "src/Pages/Company/Dashboard/pages/Widgets/shared/IconCropper";
import useIconCropper from "src/Pages/Company/Dashboard/pages/Widgets/shared/useIconCropper";

export interface EditLinkFormRef {
  submitCrop: () => Promise<void>;
  isCropping: () => boolean;
}

interface EditLinkFormProps {
  formMethods: UseFormReturn<NewLinkFormModels>;
  onCroppingChange?: (isCropping: boolean) => void;
}

const EditLinkForm = forwardRef<EditLinkFormRef, EditLinkFormProps>(
  ({ formMethods, onCroppingChange }, ref) => {
    const iconValue = useWatch({ control: formMethods.control, name: "icon" });

    const handleUploadSuccess = useCallback(
      (data: { id: string; icon: { original: string } | null }) => {
        if (data.icon) {
          formMethods.setValue("icon", data.icon, { shouldDirty: true });
          formMethods.setValue("iconId", data.id, { shouldDirty: true });
        }
      },
      [formMethods]
    );

    const {
      imgLoaded,
      setImgLoaded,
      cropperImageSrc,
      isUploading,
      fileInputRef,
      iconCropperRef,
      uploadError,
      handleFileUpload,
      handleCropped,
      handleCancelCrop,
      isCropping,
      submitCrop,
    } = useIconCropper({
      iconValue,
      onCroppingChange,
      onUploadSuccess: handleUploadSuccess,
    });

    useImperativeHandle(ref, () => ({
      isCropping,
      submitCrop,
    }));

    return (
      <FormProvider {...formMethods}>
        <Controller
          disabled={isUploading}
          name="name"
          control={formMethods.control}
          render={({ field }) => (
            <ControlledTextField
              {...field}
              data-testid="edit-link-form__name-input"
              label={__("Nazwa")}
              sx={{ width: "100%" }}
              maxLength={150}
            />
          )}
        />
        <Controller
          disabled={isUploading}
          name="url"
          control={formMethods.control}
          render={({
            field: { ref: _ref, ...field },
            fieldState: { error },
          }) => (
            <ControlledTextField
              {...field}
              data-testid="edit-link-form__url-input"
              label={__("Adres URL")}
              sx={{ width: "100%" }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Stack
          spacing="1rem"
          data-testid="edit-link-form__icon-section"
          sx={{ alignItems: "flex-start", minHeight: "100px" }}
        >
          {cropperImageSrc ? (
            <IconCropper
              ref={iconCropperRef}
              imageSrc={cropperImageSrc}
              onCancel={handleCancelCrop}
              onCropped={handleCropped}
            />
          ) : (
            <>
              {(isUploading || (iconValue && !imgLoaded)) && (
                <Skeleton
                  variant="rectangular"
                  sx={{ width: "72px", height: "72px", borderRadius: "1rem" }}
                />
              )}
              {!isUploading && iconValue && (
                <Box
                  sx={{
                    position: "relative",
                    display: imgLoaded ? "block" : "none",
                    paddingBottom: "0.5rem",
                  }}
                >
                  <img
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "1rem",
                    }}
                    src={iconValue.original}
                    alt="icon"
                    data-testid="edit-link-form__icon-preview"
                    onLoad={() => setImgLoaded(true)}
                  />
                  <IconActionMenu
                    hasIcon
                    disabled={isUploading}
                    onEdit={() => fileInputRef.current?.click()}
                    onRemove={() => {
                      formMethods.setValue("icon", null, { shouldDirty: true });
                      formMethods.setValue("iconId", null, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </Box>
              )}
              {!isUploading && !iconValue && (
                <Box sx={{ position: "relative", paddingBottom: "0.5rem" }}>
                  <Box
                    sx={{
                      "& .MuiPaper-root": {
                        maxWidth: "72px",
                        width: "72px",
                        height: "72px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "0 0 0.25rem 0",
                        padding: 0,
                      },
                    }}
                  >
                    <DefaultIconWithPaper />
                  </Box>
                  <IconActionMenu
                    hasIcon={false}
                    disabled={isUploading}
                    onEdit={() => fileInputRef.current?.click()}
                    onRemove={() => {
                      formMethods.setValue("icon", null, { shouldDirty: true });
                      formMethods.setValue("iconId", null, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </Box>
              )}
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/x-icon,image/gif,image/webp"
            hidden
            onChange={handleFileUpload}
            data-testid="edit-link-form__file-input"
          />
          {uploadError && (
            <Typography
              variant="caption"
              color="error"
              data-testid="edit-link-form__upload-error"
            >
              {uploadError}
            </Typography>
          )}
        </Stack>
      </FormProvider>
    );
  }
);

EditLinkForm.displayName = "EditLinkForm";

export default EditLinkForm;
